import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, CircleAlert, Landmark, LoaderCircle, RefreshCw } from "lucide-react";
import CopyButton from "../components/CopyButton";
import logo from "../img/logo.png";
import { buildVietQrUrl, formatVnd, paymentApi, PAYMENT_STATUS } from "../utils/paymentRequests";

function isPublicPayment(payment) {
  return payment &&
    Number.isSafeInteger(payment.amountVnd) && payment.amountVnd > 0 &&
    typeof payment.transferContent === "string" && payment.transferContent.trim() &&
    typeof payment.code === "string" &&
    ["pending", "received"].includes(payment.status) &&
    ["bankName", "bankBin", "accountName", "accountNumber"].every(
      (field) => typeof payment.bank?.[field] === "string" && payment.bank[field].trim()
    );
}

function VietQrCode({ payment }) {
  const [state, setState] = useState("loading");
  const qrUrl = buildVietQrUrl(payment);

  useEffect(() => setState("loading"), [qrUrl]);
  if (!qrUrl || state === "error") return null;

  return (
    <section aria-labelledby="vietqr-heading" className="mt-7 rounded-xl border border-[#e7dbcf] bg-white p-4 text-center sm:p-6">
      <h2 id="vietqr-heading" className="font-sans text-xs font-semibold tracking-[0.07em] !text-primary-dark">QUÉT MÃ ĐỂ CHUYỂN KHOẢN</h2>
      <p className="mt-2 text-sm leading-6 text-stone-500">Mở ứng dụng ngân hàng và quét mã VietQR bên dưới.</p>
      <div className="relative mx-auto mt-4 aspect-square w-full max-w-[320px] overflow-hidden rounded-xl border border-[#eee5dc] bg-white">
        {state === "loading" && <div role="status" className="absolute inset-0 grid place-items-center bg-[#faf7f2] text-sm text-stone-500">Đang tạo mã VietQR…</div>}
        <img
          src={qrUrl}
          alt={`Mã VietQR chuyển ${formatVnd(payment.amountVnd)} đến ${payment.bank.bankName}`}
          width="640"
          height="640"
          referrerPolicy="no-referrer"
          onLoad={() => setState("ready")}
          onError={() => setState("error")}
          className={`h-full w-full object-contain transition-opacity ${state === "ready" ? "opacity-100" : "opacity-0"}`}
        />
      </div>
      <p className="mx-auto mt-3 max-w-md text-xs leading-5 text-stone-500">Vui lòng kiểm tra lại số tiền và nội dung trên ứng dụng ngân hàng trước khi xác nhận chuyển khoản.</p>
    </section>
  );
}

function usePaymentMetadata() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Phiếu cọc | Cưng Home";
    const restoreMetadata = [
      ["robots", "noindex, nofollow, noarchive"],
      ["referrer", "no-referrer"],
    ].map(([name, content]) => {
      const existing = document.head.querySelector(`meta[name="${name}"]`);
      const element = existing || document.createElement("meta");
      const previousContent = element.getAttribute("content");
      element.setAttribute("name", name);
      element.setAttribute("content", content);
      if (!existing) document.head.appendChild(element);
      return () => {
        if (!existing) element.remove();
        else if (previousContent === null) element.removeAttribute("content");
        else element.setAttribute("content", previousContent);
      };
    });

    return () => {
      document.title = previousTitle;
      restoreMetadata.forEach((restore) => restore());
    };
  }, []);
}

export default function PaymentPage() {
  const { id } = useParams();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ id, loading: true, payment: null, error: null });
  usePaymentMetadata();

  useEffect(() => {
    let active = true;
    let controller;

    const load = async () => {
      controller?.abort();
      controller = new AbortController();
      const { signal } = controller;
      setState({ id, loading: true, payment: null, error: null });
      try {
        const payment = await paymentApi.getPublic(id, signal);
        if (!active || signal.aborted) return;
        if (payment?.status === "cancelled" || !payment) {
          setState({ id, loading: false, payment: null, error: "unavailable" });
        } else if (!isPublicPayment(payment)) {
          setState({ id, loading: false, payment: null, error: "network" });
        } else {
          setState({ id, loading: false, payment, error: null });
        }
      } catch (error) {
        if (!active || signal.aborted) return;
        setState({
          id,
          loading: false,
          payment: null,
          error: [404, 410].includes(error.status) ? "unavailable" : "network",
        });
      }
    };

    const revalidate = () => {
      if (document.visibilityState !== "hidden") load();
    };
    load();
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", revalidate);
    return () => {
      active = false;
      controller?.abort();
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", revalidate);
    };
  }, [id, attempt]);

  // A new route must never briefly display the previous customer's payment.
  const current = state.id === id ? state : { loading: true, payment: null, error: null };
  const payment = current.payment;
  const received = payment?.status === "received";

  return (
    <main className="min-h-screen bg-[#faf7f2] px-4 py-8 text-[#332d2a] sm:px-6 sm:py-12">
      <div className="mx-auto max-w-3xl">
        <div className="mb-7 flex flex-col items-center justify-center sm:mb-9">
          <img src={logo} alt="Cưng Home - sofa & more" width="460" height="144" className="h-auto w-[min(72vw,230px)] object-contain" />
          <p className="mt-2 text-xs font-semibold tracking-[0.2em] text-stone-500">PHIẾU CỌC</p>
        </div>

        {current.loading ? (
          <div role="status" className="rounded-2xl border border-[#e7dbcf] bg-white px-6 py-20 text-center shadow-sm">
            <LoaderCircle aria-hidden="true" className="mx-auto mb-4 h-7 w-7 animate-spin text-primary" />
            <p className="text-sm text-stone-600">Đang tải thông tin phiếu cọc…</p>
          </div>
        ) : current.error ? (
          <section role="alert" className="rounded-2xl border border-[#e7dbcf] bg-white px-6 py-12 text-center shadow-sm sm:px-12">
            <CircleAlert aria-hidden="true" className="mx-auto mb-5 h-10 w-10 text-primary" />
            <h1 className="text-2xl">
              {current.error === "unavailable" ? "Phiếu cọc không còn khả dụng" : "Chưa tải được phiếu cọc"}
            </h1>
            <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-stone-600">
              {current.error === "unavailable"
                ? "Liên kết có thể không tồn tại hoặc phiếu đã được hủy. Vui lòng liên hệ Cưng Home qua kênh đang trao đổi để nhận lại thông tin."
                : "Vui lòng kiểm tra kết nối mạng rồi thử lại. Nếu vẫn chưa mở được phiếu, hãy liên hệ Cưng Home qua kênh đang trao đổi."}
            </p>
            <button
              type="button"
              onClick={() => setAttempt((value) => value + 1)}
              className="mx-auto mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
            >
              <RefreshCw aria-hidden="true" size={16} /> Thử lại
            </button>
          </section>
        ) : payment ? (
          <>
            <div className="mb-7 text-center">
              <h1 className="mx-auto max-w-lg text-[1.65rem] leading-snug sm:text-3xl">Cảm ơn quý khách đã lựa chọn Cưng Home!</h1>
              <p className="mx-auto mt-3 max-w-lg text-sm leading-7 text-stone-600">
                {received
                  ? "Cưng Home đã xác nhận nhận được khoản cọc của quý khách."
                  : "Để tiến hành thanh toán, vui lòng chuyển khoản theo thông tin sau:"}
              </p>
            </div>

            <article className="overflow-hidden rounded-2xl border border-[#e7dbcf] bg-white shadow-[0_8px_36px_rgba(80,55,30,0.05)]">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#eee5dc] px-5 py-4 sm:px-8">
                <p className="break-all text-xs text-stone-500">Mã phiếu <span className="ml-1 font-semibold text-primary-dark">{payment.code}</span></p>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${received ? "bg-emerald-50 text-emerald-800" : "bg-orange-50 text-orange-800"}`}>
                  {PAYMENT_STATUS[payment.status]}
                </span>
              </div>

              <div className="p-5 sm:p-8">
                {received && (
                  <div role="status" className="mb-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
                    <CheckCircle2 aria-hidden="true" className="mt-0.5 h-6 w-6 shrink-0" />
                    <div>
                      <h2 className="font-sans text-base font-semibold !text-emerald-900">Đã nhận cọc</h2>
                      <p className="mt-1 text-sm leading-6">Quý khách không cần chuyển khoản thêm cho phiếu này.</p>
                    </div>
                  </div>
                )}

                <section aria-labelledby="payment-amount" className="rounded-xl border border-[#f0deca] bg-[#fff7ec] p-5 text-center sm:p-6">
                  <h2 id="payment-amount" className="font-sans text-xs font-semibold tracking-[0.09em] !text-primary-dark">
                    {received ? "SỐ TIỀN ĐÃ NHẬN" : "SỐ TIỀN CẦN CHUYỂN"}
                  </h2>
                  <p className="my-3 break-all text-[clamp(1.65rem,6.5vw,2.8rem)] font-bold leading-tight tabular-nums text-[#b66227]">{formatVnd(payment.amountVnd)}</p>
                  {!received && <CopyButton value={String(payment.amountVnd)} label="Sao chép số tiền" />}
                </section>

                {!received && <VietQrCode payment={payment} />}

                <section aria-labelledby="payment-bank" className="mt-7">
                  <h2 id="payment-bank" className="mb-4 flex items-center gap-2 font-sans text-xs font-semibold tracking-[0.07em] !text-primary-dark">
                    <Landmark size={17} aria-hidden="true" /> THÔNG TIN TÀI KHOẢN NHẬN
                  </h2>
                  <dl className="space-y-4 text-sm">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <dt className="text-stone-500">Ngân hàng</dt>
                      <dd className="break-words font-semibold text-stone-800">{payment.bank.bankName}</dd>
                    </div>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <dt className="text-stone-500">Tên tài khoản</dt>
                      <dd className="break-words font-semibold text-stone-800">{payment.bank.accountName}</dd>
                    </div>
                    <div className="rounded-xl border border-[#eee5dc] bg-[#fcfaf7] p-4">
                      <dt className="text-stone-500">Số tài khoản</dt>
                      <dd className="mt-1 flex flex-wrap items-center justify-between gap-3">
                        <span className="break-all text-xl font-bold tracking-wide text-primary-dark">{payment.bank.accountNumber}</span>
                        {!received && <CopyButton value={payment.bank.accountNumber} label="Sao chép số tài khoản" />}
                      </dd>
                    </div>
                  </dl>
                </section>

                <section aria-labelledby="payment-content" className="mt-7 border-t border-[#eee5dc] pt-6">
                  <h2 id="payment-content" className="font-sans text-xs font-semibold tracking-[0.07em] !text-primary-dark">NỘI DUNG CHUYỂN KHOẢN</h2>
                  <p className="my-4 whitespace-pre-wrap break-words rounded-xl border border-dashed border-[#d6b48a] bg-[#fcfaf7] px-4 py-4 text-base font-medium leading-7 text-stone-800">{payment.transferContent}</p>
                  {!received && <CopyButton value={payment.transferContent} label="Sao chép nội dung chuyển khoản" className="w-full sm:w-auto" />}
                </section>

                {!received && (
                  <section aria-labelledby="payment-instructions" className="mt-7 rounded-xl bg-[#f8f3ed] p-5">
                    <h2 id="payment-instructions" className="font-sans text-xs font-semibold tracking-[0.07em] !text-primary-dark">HƯỚNG DẪN THANH TOÁN</h2>
                    <p className="mt-3 text-sm leading-7 text-stone-600">Sau khi chuyển khoản, quý khách vui lòng gửi hình ảnh xác nhận thanh toán để chúng tôi nhanh chóng xử lý đơn hàng.</p>
                    <p className="mt-2 text-sm leading-7 text-stone-600">Quý khách có thể gửi qua Zalo hoặc kênh đang trao đổi với Cưng Home.</p>
                  </section>
                )}

                <p className="mt-7 text-center text-sm leading-7 text-stone-500">Trân trọng!<br /><strong className="font-semibold text-primary-dark">Cưng Home</strong></p>
              </div>
            </article>
          </>
        ) : null}
      </div>
    </main>
  );
}
